import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { STAFF_DEVICE_PEER_RAW, STAFF_DEVICE_RAW } from '@staff-app/graphql-client';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@staff-app/ui';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';

interface DevicePeerRaw {
  _id: string;
  publicKey: string;
  name: string;
  ownerEmail?: string | null;
  vpnIp: string;
  subnet: string;
  region: string;
  createdByRole: string;
  createdByTokenId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface StaffDevicePeerResult {
  staffDevicePeer: DevicePeerRaw | null;
}

interface StaffDeviceRawResult {
  staffDeviceRaw: string | null;
}

interface Props {
  open: boolean;
  device: MdmDevice | null;
  onClose: () => void;
}

/**
 * Raw JSON viewer for the staff MDM device drawer. Shows the full Device
 * GraphQL document (already in memory) and the corresponding wg-orchestrator
 * `mdm-devices` peer row.
 *
 * Stacking note: uses the standard `@staff-app/ui` `Dialog` wrapper at body z-50.
 * The MDM device drawer (now `InShellPanel`) sits at local z-20 inside
 * `#page-wrapper`, so this dialog naturally stacks above it via DOM order —
 * no z-index workarounds.
 */
export function DeviceJsonDialog({ open, device, onClose }: Props) {
  const [tab, setTab] = useState<'device' | 'peer'>('device');

  const { data, loading, error } = useQuery<StaffDevicePeerResult>(STAFF_DEVICE_PEER_RAW, {
    variables: { uuid: device?.UUID ?? '' },
    skip: !open || !device?.UUID,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  // Full stored device document from staff-api (every field, not the curated
  // StaffDevice projection). device._id maps directly to the collection _id.
  const { data: rawData, loading: rawLoading } = useQuery<StaffDeviceRawResult>(STAFF_DEVICE_RAW, {
    variables: { deviceId: device?._id ?? '' },
    skip: !open || !device?._id,
    fetchPolicy: 'cache-first',
  });

  // Apollo Client 4 exposes GraphQL errors on `error.errors`; the legacy
  // Apollo v3 shape used `error.graphQLErrors`. Check both to stay resilient.
  const errLike = error as
    | {
        errors?: Array<{ extensions?: Record<string, unknown> }>;
        graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
      }
    | undefined;
  const gqlErrors = errLike?.errors ?? errLike?.graphQLErrors ?? [];
  const isForbidden = gqlErrors.some((e) => e.extensions?.code === 'FORBIDDEN');
  const peerAvailable = !isForbidden;
  const peer = data?.staffDevicePeer ?? null;

  // Pretty-print the raw doc when present; fall back to the in-memory typed
  // device only if the raw fetch hasn't resolved yet.
  let deviceJson = '';
  if (rawData?.staffDeviceRaw) {
    try {
      deviceJson = JSON.stringify(JSON.parse(rawData.staffDeviceRaw), null, 2);
    } catch {
      deviceJson = rawData.staffDeviceRaw;
    }
  } else if (device) {
    deviceJson = JSON.stringify(device, null, 2);
  }
  const peerJson = peer ? JSON.stringify(peer, null, 2) : '';

  const handleCopy = async () => {
    const text = tab === 'device' ? deviceJson : peerJson;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        data-testid="mdm-device-json-dialog"
      >
        <DialogHeader>
          <DialogTitle>{'Raw device JSON'}</DialogTitle>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(next) => setTab(next as 'device' | 'peer')}
          className="mt-3 flex-1 flex flex-col overflow-hidden min-h-0"
        >
          <TabsList className="shrink-0 self-start">
            <TabsTrigger value="device" className="cursor-pointer">
              {'Device'}
            </TabsTrigger>
            {peerAvailable && (
              <TabsTrigger value="peer" className="cursor-pointer">
                {'Peer (mdm-devices)'}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="device" className="flex-1 overflow-y-auto mt-2 min-h-0">
            {rawLoading && !deviceJson ? (
              <div className="text-sm text-muted-foreground p-3">{'Loading...'}</div>
            ) : (
              <pre className="rounded-md border bg-muted p-3 text-xs font-mono whitespace-pre-wrap break-all">
                {deviceJson || 'No device loaded'}
              </pre>
            )}
          </TabsContent>

          {peerAvailable && (
            <TabsContent value="peer" className="flex-1 overflow-y-auto mt-2 min-h-0">
              {loading ? (
                <div className="text-sm text-muted-foreground p-3">{'Loading...'}</div>
              ) : peer ? (
                <pre className="rounded-md border bg-muted p-3 text-xs font-mono whitespace-pre-wrap break-all">
                  {peerJson}
                </pre>
              ) : (
                <div className="text-sm text-muted-foreground p-3">
                  {'No peer registered for this device (SecureLink not activated).'}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <Button
            variant="outline"
            size="icon-md"
            onClick={handleCopy}
            aria-label={'Copy JSON'}
            data-testid="mdm-device-json-copy"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
